/**
 * Saudi treasury defaults — cash registers, banks (SARIE), operation hierarchy, voucher types.
 * Used by caisse transfer, reconciliation, vouchers, and operation configuration pages.
 */

/** Legacy French / Algerian account keys → translation key suffix under fundsTransferPage / caisseReconciliationPage */
export const SAUDI_REGISTER_LABEL_KEYS = {
  'Caisse Principale': 'register_main',
  'Caisse Secondaire': 'register_secondary',
  'Caisse Succursale A': 'register_branch_a',
  'Caisse Succursale B': 'register_branch_b',
  'Caisse Succursale C': 'register_branch_c',
  'Caisse Mensuelle': 'register_branch_a',
  'Journal CN°03/A.B': 'register_branch_b',
  'الصندوق الرئيسي': 'register_main',
  'صندوق فرعي': 'register_branch_a',
  'صندوق فرعي ثانوي': 'register_secondary',
  'Al Rajhi Bank': 'bank_alrajhi',
  'Saudi National Bank': 'bank_snb',
  'Riyad Bank': 'bank_riyad',
  'Alinma Bank': 'bank_alinma',
  'SARIE Transfer': 'bank_sarie',
};

export const SAUDI_DEFAULT_REGISTERS = [
  { value: 'الصندوق الرئيسي', labelKey: 'register_main' },
  { value: 'صندوق فرعي', labelKey: 'register_branch_a' },
  { value: 'صندوق فرعي ثانوي', labelKey: 'register_secondary' },
];

export const SAUDI_DEFAULT_BANK_ACCOUNTS = [
  { value: 'Al Rajhi Bank', labelKey: 'bank_alrajhi' },
  { value: 'Saudi National Bank', labelKey: 'bank_snb' },
  { value: 'Riyad Bank', labelKey: 'bank_riyad' },
  { value: 'SARIE Transfer', labelKey: 'bank_sarie' },
];

export const SAUDI_TRANSFER_MOTIF_KEYS = [
  'motif_internal_transfer',
  'motif_sarie_deposit',
  'motif_cash_withdrawal',
  'motif_branch_replenishment',
  'motif_zakat_charity',
];

export const SAUDI_RESPONSIBLE_DEFAULTS = [
  'محمد العتيبي',
  'خالد الشمري',
  'فهد القحطاني',
];

/** Operation code types (hierarchy level 1) */
export const SAUDI_CODE_TYPES = [
  { id: 'CN', name: 'صندوق نقدي', description: 'رموز الصناديق النقدية والفروع' },
  { id: 'ADM', name: 'إدارية', description: 'معاملات ومصروفات إدارية' },
  { id: 'DIV', name: 'متنوعة', description: 'مصروفات متنوعة وتشغيلية' },
  { id: 'PR', name: 'أسطول المركبات', description: 'مصروفات المركبات والوقود والصيانة' },
  { id: 'GRH', name: 'الموارد البشرية', description: 'رواتب وسلف وبدلات الموظفين' },
  { id: 'PRJ', name: 'مشروع / موقع', description: 'مصروفات المواقع والمشاريع' },
];

/** Debit (payment) operation categories — Saudi business context */
export const SAUDI_DEBIT_CATEGORIES = [
  {
    id: 1,
    name: 'الرواتب والسلف',
    codeTypeId: 'GRH',
    operations: [
      'صرف راتب شهري',
      'سلفة على الراتب',
      'مكافأة أو حافز',
      'أجر يومي أو أسبوعي',
      'بدل انتقال',
    ],
  },
  {
    id: 2,
    name: 'السفر والمهمات',
    codeTypeId: 'PR',
    operations: [
      'مصاريف سفر (محلي)',
      'مصاريف سفر (بين المدن)',
      'تذاكر طيران / حجز نقل',
      'مهمة فنية أو إدارية',
      'إقامة فندقية',
    ],
  },
  {
    id: 3,
    name: 'المركبات والصيانة',
    codeTypeId: 'PR',
    operations: [
      'تغيير زيت',
      'وقود / بنزين',
      'إصلاح إطارات',
      'صيانة دورية',
      'غسيل مركبة',
    ],
  },
  {
    id: 4,
    name: 'المكتب والقرطاسية',
    codeTypeId: 'ADM',
    operations: [
      'شراء ورق A4',
      'حبر طابعة',
      'قرطاسية مكتبية',
      'معدات تقنية',
    ],
  },
  {
    id: 5,
    name: 'مصروفات تشغيلية',
    codeTypeId: 'DIV',
    operations: [
      'مواد تنظيف',
      'مستلزمات مكتب',
      'صيانة عامة',
      'خدمات مقاول فرعي',
      'رسوم حكومية / تجديد تراخيص',
    ],
  },
  {
    id: 6,
    name: 'زكاة وصدقات',
    codeTypeId: 'DIV',
    operations: [
      'صدقة / تبرع',
      'زكاة مستحقة',
      'مساعدة اجتماعية',
    ],
  },
];

/** Credit (receipt) operation categories */
export const SAUDI_CREDIT_CATEGORIES = [
  {
    id: 1,
    name: 'تعزيز الصندوق',
    codeTypeId: 'CN',
    operations: [
      'تغذية الصندوق الرئيسي',
      'تغذية صندوق فرعي',
      'إرجاع فائض للصندوق الرئيسي',
      'سحب نقدي من البنك (SARIE)',
    ],
  },
  {
    id: 2,
    name: 'المبالغ المستردة والتسويات',
    codeTypeId: 'DIV',
    operations: [
      'استرداد من موظف',
      'استرداد مصروفات متنوعة',
      'تسوية سلفة',
      'إيداع بنكي SARIE',
    ],
  },
];

/** Flat debit categories for OperationTypesPage (no codeTypeId) */
export const SAUDI_DEBIT_CATEGORIES_FLAT = SAUDI_DEBIT_CATEGORIES.map(({ id, name, operations }) => ({
  id,
  name,
  operations,
}));

export const SAUDI_CREDIT_CATEGORIES_FLAT = SAUDI_CREDIT_CATEGORIES.map(({ id, name, operations }) => ({
  id,
  name,
  operations,
}));

/** Voucher UI type ids */
export const SAUDI_VOUCHER_TYPES = [
  { value: 'petty_cash', labelKey: 'type_petty_cash' },
  { value: 'expense', labelKey: 'type_expense' },
  { value: 'advance', labelKey: 'type_advance' },
];

/** Legacy French / Arabic unit codes → caisseJournalPage translation key suffix */
export const JOURNAL_UNIT_LABEL_KEYS = {
  unité: 'unit_piece',
  unite: 'unit_piece',
  وحدة: 'unit',
  قطعة: 'unit_piece',
  pièce: 'unit_piece',
  piece: 'unit_piece',
  pcs: 'unit_pcs',
  heure: 'unit_hour',
  h: 'unit_hour',
  jour: 'unit_day',
  j: 'unit_day',
  semaine: 'unit_week',
  mois: 'unit_month',
  année: 'unit_year',
  annee: 'unit_year',
  minute: 'unit_minute',
  min: 'unit_minute',
  seconde: 'unit_second',
  s: 'unit_second',
  kg: 'unit_kg',
  g: 'unit_g',
  mg: 'unit_mg',
  tonne: 'unit_tonne',
  t: 'unit_tonne',
  quintal: 'unit_quintal',
  q: 'unit_quintal',
  L: 'unit_l',
  litre: 'unit_l',
  mL: 'unit_ml',
  millilitre: 'unit_ml',
  m: 'unit_m',
  mètre: 'unit_m',
  metre: 'unit_m',
  cm: 'unit_cm',
  centimètre: 'unit_cm',
  centimetre: 'unit_cm',
  mm: 'unit_mm',
  millimètre: 'unit_mm',
  millimetre: 'unit_mm',
  km: 'unit_km',
  kilomètre: 'unit_km',
  kilometre: 'unit_km',
  m2: 'unit_m2',
  'm²': 'unit_m2',
  m3: 'unit_m3',
  'm³': 'unit_m3',
  ha: 'unit_ha',
  hectare: 'unit_ha',
  carton: 'unit_carton',
  palette: 'unit_palette',
  caisse: 'unit_box',
  boîte: 'unit_box',
  boite: 'unit_box',
  sachet: 'unit_sachet',
  sac: 'unit_bag',
  paquet: 'unit_pack',
  lot: 'unit_lot',
  paire: 'unit_pair',
  douzaine: 'unit_dozen',
  centaine: 'unit_hundred',
  millier: 'unit_thousand',
  rouleau: 'unit_roll',
  barre: 'unit_bar',
  plaque: 'unit_plate',
  feuille: 'unit_sheet',
  tube: 'unit_tube',
  bidon: 'unit_can',
  jerrycan: 'unit_jerrycan',
  bouteille: 'unit_bottle',
  flacon: 'unit_flask',
  pot: 'unit_pot',
  kWh: 'unit_kwh',
  Wh: 'unit_wh',
  kW: 'unit_kw',
  W: 'unit_w',
  ampère: 'unit_ampere',
  ampere: 'unit_ampere',
  A: 'unit_ampere',
  volt: 'unit_volt',
  V: 'unit_volt',
  cL: 'unit_cl',
  centilitre: 'unit_centilitre',
  dL: 'unit_dl',
  décilitre: 'unit_decilitre',
  decilitre: 'unit_decilitre',
  hL: 'unit_hl',
  hectolitre: 'unit_hectolitre',
  'cm²': 'unit_cm2',
  cm2: 'unit_cm2',
  'km²': 'unit_km2',
  km2: 'unit_km2',
  are: 'unit_are',
  'cm³': 'unit_cm3',
  cm3: 'unit_cm3',
  'dm³': 'unit_dm3',
  dm3: 'unit_dm3',
  spray: 'unit_spray',
  aérosol: 'unit_aerosol',
  aerosol: 'unit_aerosol',
};

/** Resolve a stored journal label (French/Arabic legacy value) to a caisseJournalPage i18n key suffix. */
export function resolveJournalLabelKey(val) {
  if (val == null || val === '') return null;
  const trimmed = String(val).trim();
  if (JOURNAL_LEGACY_LABEL_KEYS[trimmed]) return JOURNAL_LEGACY_LABEL_KEYS[trimmed];
  const lower = trimmed.toLowerCase();
  for (const [key, labelKey] of Object.entries(JOURNAL_LEGACY_LABEL_KEYS)) {
    if (key.toLowerCase() === lower) return labelKey;
  }
  return null;
}

/** Resolve a stored unit code (French legacy value) to a caisseJournalPage i18n key suffix. */
export function resolveJournalUnitKey(val) {
  if (val == null || val === '') return null;
  const trimmed = String(val).trim();
  if (JOURNAL_UNIT_LABEL_KEYS[trimmed]) return JOURNAL_UNIT_LABEL_KEYS[trimmed];
  const lower = trimmed.toLowerCase();
  if (JOURNAL_UNIT_LABEL_KEYS[lower]) return JOURNAL_UNIT_LABEL_KEYS[lower];
  for (const [key, labelKey] of Object.entries(JOURNAL_UNIT_LABEL_KEYS)) {
    if (key.toLowerCase() === lower) return labelKey;
  }
  return null;
}

/** Legacy French journal labels → caisseJournalPage translation key suffix */
export const JOURNAL_LEGACY_LABEL_KEYS = {
  'CN - Caisse Numéro': 'cat_cn_caisse_numero',
  'ADM - Administration': 'cat_adm_administration',
  'DIV - Divers': 'cat_div_divers',
  'PR - Parc Roulant': 'cat_pr_parc_roulant',
  'Paiements salaires et avances': 'subcat_salary_advances',
  'Retour de fonds vers caisse principale': 'subcat_return_main_fund',
  'Achats bureautiques': 'subcat_office_supplies',
  'Achats divers / fonctionnement': 'subcat_misc_operating',
  'Déplacements et missions': 'subcat_travel_missions',
  'Achats véhicules et entretien': 'subcat_vehicle_maintenance',
  'Alimentations de caisse': 'subcat_cash_feeding',
  'Alimentation caisse principale': 'subcat_main_cash_feeding',
  'Remboursements & régularisations': 'subcat_refunds_adjustments',
  'Paiement salaire mensuel': 'des_monthly_salary',
  'Avance sur salaire': 'des_salary_advance',
  'Paiement femme de ménage': 'des_housekeeping_payment',
  'Paiement journalier ou hebdomadaire': 'des_daily_weekly_wage',
  "Paiement d'indemnité ou prime": 'des_bonus_payment',
  'Transfert de liquidités': 'des_cash_transfer',
  'Dépôt en caisse principale': 'des_deposit_main_cash',
  'Alimentation de caisse (par employé)': 'des_cash_feed_by_employee',
  'Retrait espèces banque': 'des_bank_cash_withdrawal',
  'Entrée de liquidités': 'des_cash_inflow',
  'Alimentation de caisse principale': 'des_main_cash_feeding',
  'Versement en caisse principale': 'des_payment_main_cash',
  'Achat RAM papier': 'des_buy_a4_paper',
  'Achat toner imprimante': 'des_buy_printer_toner',
  'Achat cartouche': 'des_buy_cartridge',
  'Achat plastifieuse / plastification': 'des_buy_laminator',
  'Achat enveloppes': 'des_buy_envelopes',
  'Achat badge / impression badge': 'des_buy_badge_print',
  'Achat matériel informatique': 'des_buy_it_equipment',
  'Achat fournitures bureau': 'des_buy_office_supplies',
  'Achat consommables (gants, masques…)': 'des_buy_consumables',
  'Achat produits nettoyage': 'des_buy_cleaning_products',
  'Achat petit matériel (balais, brosses…)': 'des_buy_small_equipment',
  'Achat mobilier ou réparation': 'des_buy_furniture_repair',
  'Frais de déplacement (local)': 'des_travel_local',
  'Frais de déplacement (inter-régional)': 'des_travel_intercity',
  'Transport personnel sans véhicule dispo': 'des_personal_transport',
  'Mission technique ou administrative': 'des_technical_mission',
  "Billet d'avion / réservation transport": 'des_flight_transport_booking',
  'Vidange moteur': 'des_oil_change',
  'Achat huile moteur': 'des_buy_engine_oil',
  'Achat filtre à huile / gasoil / air': 'des_buy_oil_filter',
  'Réparation pneu': 'des_tire_repair',
  'Achat chambre à air': 'des_buy_inner_tube',
  'Achat batterie véhicule': 'des_buy_vehicle_battery',
  'Lavage véhicule': 'des_vehicle_wash',
  'Achat pièces mécaniques': 'des_buy_mechanical_parts',
  "Frais de main-d'œuvre mécanique": 'des_mechanic_labor',
  'Remboursement personnel': 'des_staff_refund',
  'Remboursement dépenses diverses': 'des_misc_expense_refund',
  'Remboursement par salarié (avance)': 'des_employee_advance_refund',
  'Remboursement erreur ou double dépense': 'des_error_refund',
  'Dépenses diverses': 'aff_desc_misc_expenses',
  'Gestion des véhicules': 'aff_desc_vehicle_mgmt',
  'Chantier 16 de 2023': 'aff_desc_site_16_2023',
  'GRH - Ressources Humaines': 'cat_grh_hr',
  'PRJ - Projet / Chantier': 'cat_prj_project',
  'Remboursement frais de mission': 'des_mission_expense_refund',
  'Frais de contrôle technique': 'des_technical_inspection',
  'Carburant / وقود': 'des_fuel',
  'Achat spots LED': 'des_buy_led_spots',
  'Achat transformateur LED': 'des_buy_led_transformer',
  'Achat tube néon LED': 'des_buy_led_neon_tube',
  'Achat plaque LED': 'des_buy_led_panel',
  'Disjoncteur / différentiel': 'des_buy_circuit_breaker',
  'Klixon / thermostat / relais': 'des_buy_thermostat_relay',
  'Câbles, goulottes, visserie': 'des_buy_cables_hardware',
  'Outillage électrique (fer à souder, pince, etc.)': 'des_buy_electrical_tools',
  'Achat briques (par palette)': 'des_buy_bricks_palette',
  'Achat sacs de ciment': 'des_buy_cement_bags',
  'Achat sable': 'des_buy_sand',
  'Frais de déchargement (manitou)': 'des_unloading_fee',
  'Achat enduit / peinture / plâtre': 'des_buy_paint_plaster',
  'Achat colle / fixateur / ruban / cheville': 'des_buy_adhesive_fixings',
  'Achat échelle / escabeau': 'des_buy_ladder',
  'Achat outillage de chantier (mèche, pince, etc.)': 'des_buy_site_tools',
  'Achat mobilier ou réparation (chaise, WC, etc.)': 'des_buy_furniture_repair_detail',
  'Achat pièces réfrigérateur': 'des_buy_fridge_parts',
  'Paiement éléctricien / plombier': 'des_electrician_plumber_payment',
  'Paiement sous-traitant': 'des_subcontractor_payment',
  "Location d'appartement": 'des_apartment_rental',
  'Location de véhicule': 'des_vehicle_rental',
  'Envoi courrier recommandé': 'des_registered_mail',
  'Paiement transporteur (pièces, matériel…)': 'des_carrier_payment',
  'Versement pour association': 'des_association_donation',
  'Amende et frais de justice': 'des_fines_legal_fees',
  'Frais bancaires / taxe versement': 'des_bank_fees',
  'Dîner / réception professionnelle': 'des_business_reception',
  'Marge bénéficiaire salarié': 'des_employee_margin',
  'Achat denrées alimentaires (fruits/légumes)': 'des_buy_food_produce',
  'Paiement facture électricité': 'des_electricity_bill',
  'Paiement facture eau': 'des_water_bill',
  'Paiement facture internet': 'des_internet_bill',
  'Paiement facture téléphone': 'des_phone_bill',
  'Achat matériel de sécurité (EPI)': 'des_buy_ppe',
  'Achat extincteurs': 'des_buy_extinguishers',
  'Maintenance climatisation': 'des_ac_maintenance',
  'Achat fournitures sanitaires': 'des_buy_sanitary_supplies',
  'Paiement assurance véhicule': 'des_vehicle_insurance',
  'Paiement assurance multirisque': 'des_multirisk_insurance',
  'Paiement impôts et taxes': 'des_taxes_payment',
  'Cotisations sociales GOSI': 'des_gosi_contributions',
  'Paiement GOSI': 'des_gosi_payment',
  'Retrait نقداً banque': 'des_bank_cash_withdrawal',
  'Dépôt نقداً banque': 'des_cash_deposit_bank',
  'Intérêts bancaires': 'des_bank_interest',
  'Pénalités de retard': 'des_late_penalties',
  'Achat logiciel': 'des_buy_software',
  'Achat licence software': 'des_buy_software_license',
  'Remboursement client': 'des_client_refund',
  'Frais douane et port': 'des_customs_port_fees',
  'Frais de port': 'des_shipping_fees',
  "Frais d'enregistrement commercial": 'des_commercial_registration_fees',
  'Location matériel de chantier': 'des_site_equipment_rental',
  'Paiement service de gardiennage': 'des_security_service_payment',
  'Achat snacks / boissons': 'des_buy_snacks_drinks',
  'Frais représentation commerciale': 'des_commercial_representation',
  'Frais publicité': 'des_advertising_fees',
  'Achat cartes de visite': 'des_buy_business_cards',
  'Frais formation': 'des_training_fees',
  'Frais séminaire': 'des_seminar_fees',
  'Frais visa': 'des_visa_fees',
  'Recharge carburant station': 'des_fuel_station_refill',
  'Paiement timbre fiscal': 'des_fiscal_stamp_payment',
  'Frais notaire': 'des_notary_fees',
  'Acquisition terrain': 'des_land_acquisition',
  // Saudi Arabic operation names (stored as-is in new data)
  'صرف راتب شهري': 'des_monthly_salary',
  'سلفة على الراتب': 'des_salary_advance',
  'مكافأة أو حافز': 'des_bonus_payment',
  'أجر يومي أو أسبوعي': 'des_daily_weekly_wage',
  'بدل انتقال': 'des_travel_allowance',
  'مصاريف سفر (محلي)': 'des_travel_local',
  'مصاريف سفر (بين المدن)': 'des_travel_intercity',
  'تذاكر طيران / حجز نقل': 'des_flight_transport_booking',
  'مهمة فنية أو إدارية': 'des_technical_mission',
  'إقامة فندقية': 'des_hotel_stay',
  'تغيير زيت': 'des_oil_change',
  'وقود / بنزين': 'des_fuel',
  'إصلاح إطارات': 'des_tire_repair',
  'صيانة دورية': 'des_periodic_maintenance',
  'غسيل مركبة': 'des_vehicle_wash',
  'شراء ورق A4': 'des_buy_a4_paper',
  'حبر طابعة': 'des_buy_printer_toner',
  'قرطاسية مكتبية': 'des_buy_office_supplies',
  'معدات تقنية': 'des_buy_it_equipment',
  'مواد تنظيف': 'des_buy_cleaning_products',
  'مستلزمات مكتب': 'des_buy_office_supplies_misc',
  'صيانة عامة': 'des_general_maintenance',
  'خدمات مقاول فرعي': 'des_subcontractor_services',
  'رسوم حكومية / تجديد تراخيص': 'des_government_fees',
  'صدقة / تبرع': 'des_charity_donation',
  'زكاة مستحقة': 'des_zakat_due',
  'مساعدة اجتماعية': 'des_social_aid',
  'تغذية الصندوق الرئيسي': 'des_main_cash_feeding',
  'تغذية صندوق فرعي': 'des_branch_cash_feeding',
  'إرجاع فائض للصندوق الرئيسي': 'subcat_return_main_fund',
  'سحب نقدي من البنك (SARIE)': 'des_bank_cash_withdrawal',
  'استرداد من موظف': 'des_staff_refund',
  'استرداد مصروفات متنوعة': 'des_misc_expense_refund',
  'تسوية سلفة': 'des_advance_settlement',
  'إيداع بنكي SARIE': 'des_sarie_bank_deposit',
  // Arabic designation labels (stored as-is in new journal data)
  'تغذية الصندوق (بواسطة موظف)': 'des_cash_feed_by_employee',
  'أجر خدمة منزلية': 'des_housekeeping_payment',
  'نقل شخصي بدون مركبة متاحة': 'des_personal_transport',
  'استرداد مصاريف مهمة': 'des_mission_expense_refund',
  'شراء زيت محرك': 'des_buy_engine_oil',
  'شراء فلتر زيت / ديزل / هواء': 'des_buy_oil_filter',
  'شراء أنبوب داخلي': 'des_buy_inner_tube',
  'شراء بطارية مركبة': 'des_buy_vehicle_battery',
  'شراء قطع ميكانيكية': 'des_buy_mechanical_parts',
  'أجور يد عاملة ميكانيكية': 'des_mechanic_labor',
  'رسوم الفحص الفني': 'des_technical_inspection',
  'شراء خرطوشة': 'des_buy_cartridge',
  'شراء آلة تغليف': 'des_buy_laminator',
  'شراء أظرف': 'des_buy_envelopes',
  'شراء بطاقة / طباعة بطاقة': 'des_buy_badge_print',
  'شراء معدات تقنية': 'des_buy_it_equipment',
  'شراء إضاءة LED': 'des_buy_led_spots',
  'شراء محوّل LED': 'des_buy_led_transformer',
  'شراء أنبوب نيون LED': 'des_buy_led_neon_tube',
  'شراء لوحة LED': 'des_buy_led_panel',
  'قاطع / تفاضلي': 'des_buy_circuit_breaker',
  'ترموستات / مرحّل / قاطع حراري': 'des_buy_thermostat_relay',
  'كابلات ومجرات وبراغي': 'des_buy_cables_hardware',
  'أدوات كهربائية (كاوية لحام، كماشة…)': 'des_buy_electrical_tools',
  'شراء طوب (بالباليت)': 'des_buy_bricks_palette',
  'شراء أكياس إسمنت': 'des_buy_cement_bags',
  'شراء رمل': 'des_buy_sand',
  'رسوم التفريغ (رافعة)': 'des_unloading_fee',
  'شراء معجون / دهان / جبس': 'des_buy_paint_plaster',
  'شراء لاصق / مثبّت / شريط / مسامير': 'des_buy_adhesive_fixings',
  'شراء سلم / منصة': 'des_buy_ladder',
  'شراء أدوات موقع (مثقاب، كماشة…)': 'des_buy_site_tools',
  'مستهلكات (قفازات، أقنعة…)': 'des_buy_consumables',
  'أدوات صغيرة (مكانس، فرش…)': 'des_buy_small_equipment',
  'شراء أثاث أو إصلاح (كرسي، مرحاض…)': 'des_buy_furniture_repair_detail',
  'شراء قطع ثلاجة': 'des_buy_fridge_parts',
  'دفع كهربائي / سبّاك': 'des_electrician_plumber_payment',
  'دفع مقاول فرعي': 'des_subcontractor_payment',
  'إيجار شقة': 'des_apartment_rental',
  'إيجار مركبة': 'des_vehicle_rental',
  'إرسال بريد مسجّل': 'des_registered_mail',
  'دفع ناقل (قطع، مواد…)': 'des_carrier_payment',
  'استرداد سلفة موظف': 'des_employee_advance_refund',
  'استرداد خطأ أو مصروف مزدوج': 'des_error_refund',
  'تبرع لجمعية': 'des_association_donation',
  'غرامة ورسوم قضائية': 'des_fines_legal_fees',
  'رسوم بنكية / ضريبة تحويل': 'des_bank_fees',
  'عشاء / استقبال مهني': 'des_business_reception',
  'هامش ربح موظف': 'des_employee_margin',
  'شراء مواد غذائية (فواكه/خضار)': 'des_buy_food_produce',
  'دفع فاتورة كهرباء': 'des_electricity_bill',
  'دفع فاتورة مياه': 'des_water_bill',
  'دفع فاتورة إنترنت': 'des_internet_bill',
  'دفع فاتورة هاتف': 'des_phone_bill',
  'معدات وقاية شخصية': 'des_buy_ppe',
  'شراء طفايات حريق': 'des_buy_extinguishers',
  'صيانة تكييف': 'des_ac_maintenance',
  'شراء مستلزمات صحية': 'des_buy_sanitary_supplies',
  'دفع تأمين مركبة': 'des_vehicle_insurance',
  'دفع تأمين شامل': 'des_multirisk_insurance',
  'دفع ضرائب ورسوم': 'des_taxes_payment',
  'اشتراكات GOSI': 'des_gosi_contributions',
  'دفع GOSI': 'des_gosi_payment',
  'إيداع نقدي في البنك': 'des_cash_deposit_bank',
  'فوائد بنكية': 'des_bank_interest',
  'غرامات تأخير': 'des_late_penalties',
  'شراء برنامج': 'des_buy_software',
  'شراء ترخيص برنامج': 'des_buy_software_license',
  'استرداد للعميل': 'des_client_refund',
  'رسوم جمرك وميناء': 'des_customs_port_fees',
  'رسوم شحن': 'des_shipping_fees',
  'رسوم السجل التجاري': 'des_commercial_registration_fees',
  'إيجار معدات موقع': 'des_site_equipment_rental',
  'دفع خدمة حراسة': 'des_security_service_payment',
  'شراء وجبات خفيفة / مشروبات': 'des_buy_snacks_drinks',
  'مصاريف تمثيل تجاري': 'des_commercial_representation',
  'مصاريف إعلان': 'des_advertising_fees',
  'شراء بطاقات عمل': 'des_buy_business_cards',
  'مصاريف تدريب': 'des_training_fees',
  'مصاريف ندوة': 'des_seminar_fees',
  'مصاريف تأشيرة': 'des_visa_fees',
  'تعبئة وقود من محطة': 'des_fuel_station_refill',
  'دفع طابع جبائي': 'des_fiscal_stamp_payment',
  'رسوم كاتب عدل': 'des_notary_fees',
  'شراء أرض': 'des_land_acquisition',
  باليت: 'unit_palette',
  // Saudi Arabic categories
  'الرواتب والسلف': 'subcat_salary_advances',
  'السفر والمهمات': 'subcat_travel_missions',
  'المركبات والصيانة': 'subcat_vehicle_maintenance',
  'المكتب والقرطاسية': 'subcat_office_supplies',
  'مصروفات تشغيلية': 'subcat_misc_operating',
  'زكاة وصدقات': 'subcat_zakat_charity',
  'تعزيز الصندوق': 'subcat_cash_feeding',
  'المبالغ المستردة والتسويات': 'subcat_refunds_adjustments',
  'صندوق نقدي': 'cat_cn_caisse_numero_short',
  'إدارية': 'cat_adm_administration_short',
  'متنوعة': 'cat_div_divers_short',
  'أسطول المركبات': 'cat_pr_parc_roulant_short',
  'الموارد البشرية': 'cat_grh_hr_short',
  'مشروع / موقع': 'cat_prj_project_short',
};
